import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { MEDIA, WHATSAPP_URL } from "../../config/site";
import { useEnquiry } from "../../lib/site-ui";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["DGCA Ground Classes", "Built for Aspiring Pilots"];

export const Hero = () => {
  const { openEnquiry } = useEnquiry();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section id="home" ref={ref} className="relative isolate min-h-[100svh] flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img src={MEDIA.hero} alt="Aircraft wing above the clouds at sunrise — AeroVaid Aviation Academy DGCA ground coaching" className="w-full h-[120%] object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40" />
      <div className="absolute inset-0 -z-10 hairline-grid opacity-40" />

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 pb-16 sm:pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
          className="label-mono text-slate-300 mb-6 flex items-center gap-3"
        >
          <span className="h-px w-8 bg-slate-400" />
          Online Across India · Personalised Coaching in Mumbai
        </motion.p>

        <h1 aria-label="DGCA Ground Classes Built for Aspiring Pilots" className="font-display text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tighter text-white max-w-5xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="hero-line-mask">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-7 max-w-xl text-lg text-slate-300 leading-relaxed"
        >
          Learn smarter. Understand the concepts. Prepare confidently for your DGCA examinations.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-demo-btn"
            onClick={() => openEnquiry()}
            className="bg-white text-slate-950 font-semibold px-8 py-4 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.02] text-sm tracking-wide"
          >
            BOOK YOUR FREE DEMO CLASS
          </button>
          <a
            href={WHATSAPP_URL} target="_blank" rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" /> WHATSAPP US
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-8 label-mono text-slate-400"
        >
          Affordable · Personalised · Exam-focused
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 right-6 hidden sm:flex items-center gap-2 text-slate-400 text-xs"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" /> Scroll
      </motion.div>
    </section>
  );
};
